import {
  IStepValueAboutMyself,
  TWizardStepComponentEditable,
  WIZARD_STUDENT_STEPS_PATHS,
  TStepPayload,
  TStepErrors,
  TWizardStepComponentEditableView,
  IWizardStudentState
} from "../../../interfaces";
import { Form, Input, Select } from "antd";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { TLang } from "../../../../../assets/data/isoNameLanguage";
import { getLanguage } from "../../../../../utils/fetchMapInfo";
import { useTranslation } from "react-i18next";
import { UserAPI } from "../../../../../API/userApi";
import { getChangedFields, getStepErrorsFromResponse } from "../../../utils";
import {
  StudentCategorizationValues,
  TStudentCategorization
} from "../../../../../interfaces/interfaces";
import { useContextHospi } from "../../../../../context/ContextHospi";
import styled from "styled-components/macro";
import { StepViewOnlyValue } from "../../styles";
import {
  Sweeping,
  Sports,
  Conversation,
  Ecological,
  MountenClimbing,
  Pets,
  ShakingHands,
  TeaCup
} from "components/Icons/Wizard/TenantPreferences/AllTenantPreferences";
import { WizardButtonSelect } from "../../../components";

const statePath = WIZARD_STUDENT_STEPS_PATHS.AboutMyself;

const CATEGORIZATION_ICONS: {
  [key in TStudentCategorization]: React.ReactNode;
} = {
  clean: <Sweeping />,
  quiet: <Conversation />,
  eco_friendly: <Ecological />,
  animal_friendly: <Pets />,
  social: <TeaCup />,
  pro_active: <Sports />,
  honest: <ShakingHands />,
  outgoing: <MountenClimbing />
};

export const WizardStepAboutMyself: TWizardStepComponentEditable<IWizardStudentState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IStepValueAboutMyself>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] =
      useState<TStepErrors<IStepValueAboutMyself>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return UserAPI.wizardStudentSaveAboutMyself(values)
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return UserAPI.wizardStudentSaveAboutMyselfPartially(
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepAboutMyselfView
        values={values}
        errors={errors}
        setValues={setValues}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const Root = styled(Form)``;

export const WizardStepAboutMyselfView: TWizardStepComponentEditableView<IStepValueAboutMyself> =
  React.memo(({ values, setValues, errors, readonly, viewonly }) => {
    const { currentUserRole } = useContextHospi();
    const { t, i18n } = useTranslation(currentUserRole);
    const currentLanguage: TLang = i18n.language.toUpperCase() as TLang;
    const allLanguagesOptions = getLanguage(currentLanguage);
    const inputSize = "large";

    const categorizationOptions = StudentCategorizationValues.map((v) => ({
      label: (
        t(`${currentUserRole}Profile.aboutMeInfo.form.categorization.options`, {
          returnObjects: true
        }) as { [key: string]: string }
      )[v],
      value: v,
      icon: CATEGORIZATION_ICONS[v]
    }));

    return (
      <Root layout="vertical">
        <Form.Item
          label={t(
            `${currentUserRole}Profile.aboutMeInfo.form.spokenLanguage.title`
          )}
          validateStatus={errors?.spoken_languages ? "error" : ""}
          help={errors?.spoken_languages}
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {allLanguagesOptions
                .filter((c) =>
                  values.spoken_languages?.includes(c.value as string)
                )
                ?.map((o) => o.label)
                .join(", ") ?? ""}
            </StepViewOnlyValue>
          ) : (
            <Select
              size={inputSize}
              mode="multiple"
              disabled={readonly}
              value={values.spoken_languages ?? undefined}
              placeholder={t(
                `${currentUserRole}Profile.aboutMeInfo.form.spokenLanguage.placeholder`
              )}
              options={allLanguagesOptions}
              onChange={(newValue) =>
                setValues({ ...values, spoken_languages: newValue })
              }
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(
            `${currentUserRole}Profile.aboutMeInfo.form.categorization.title`
          )}
          validateStatus={errors?.categorization ? "error" : ""}
          help={errors?.categorization}
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {categorizationOptions
                .filter((c) => values.categorization?.includes(c.value))
                ?.map((o) => o.label)
                .join(", ") ?? ""}
            </StepViewOnlyValue>
          ) : (
            <WizardButtonSelect
              disabled={readonly}
              value={values.categorization ?? undefined}
              options={categorizationOptions}
              onChange={(newValue) =>
                setValues({
                  ...values,
                  categorization: newValue
                })
              }
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(
            `${currentUserRole}Profile.aboutMeInfo.form.aboutYourself.title`
          )}
          validateStatus={errors?.description ? "error" : ""}
          help={errors?.description}
        >
          {viewonly ? (
            <StepViewOnlyValue>{values.description ?? ""}</StepViewOnlyValue>
          ) : (
            <Input.TextArea
              size={inputSize}
              disabled={readonly}
              value={values.description ?? undefined}
              placeholder={t(
                `${currentUserRole}Profile.aboutMeInfo.form.aboutYourself.placeholder`
              )}
              onChange={(e) =>
                setValues({ ...values, description: e.target.value })
              }
              rows={3}
            />
          )}
        </Form.Item>
      </Root>
    );
  });
